import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import CustomSelect from "~components/CustomSelect";
import FilterSelectFixtureStatus from "~components/FilterSelectFixtureStatus/FilterSelectFixtureStatus.tsx";
import { SelectPool } from "~components/SelectPool/SelectPool";
import { SelectSeries } from "~components/SelectSeries/SelectSeries";
import { SelectStage } from "~components/SelectStage/SelectStage";
import { SelectVenue } from "~components/SelectVenue/SelectVenue";
import { apiDateFormat } from "~constants/apiDateFormat.ts";
import type { FixtureStatuses } from "~enums/fixtureStatuses";
import { FixtureTypes } from "~enums/fixtureTypes";
import classNames from "~utils/classNames";
import type { AssignFixtureOfficialsFiltersType } from "../../types/assignFixtureOfficialsFilters";
import styles from "./AssignFixtureOfficialsFilters.module.css";

type Props = {
  filters: AssignFixtureOfficialsFiltersType;
  setFilters: (newFilters: Partial<AssignFixtureOfficialsFiltersType>) => void;
  isFetching: boolean;
  seasonDates?: {
    fromTimeLocal?: string;
    toTimeLocal?: string;
  };
  className?: string;
};

export const AssignFixtureOfficialsFilters = ({ filters, setFilters, isFetching, seasonDates, className }: Props) => {
  const { formatMessage } = useIntl();

  const selectProps = {
    allowClear: true,
    className: styles.select,
    disabled: isFetching,
  };

  const { seasonId = "" } = useParams();

  return (
    <div className={`${classNames(className, styles.filters)}`}>
      <DatePicker
        disabled={isFetching}
        minDate={dayjs(seasonDates?.fromTimeLocal, apiDateFormat)}
        maxDate={dayjs(seasonDates?.toTimeLocal, apiDateFormat)}
        value={filters.fromTimeLocal}
        onChange={(fromTimeLocal) => setFilters({ fromTimeLocal })}
      />
      <CustomSelect
        options={Object.values(FixtureTypes).map((type) => ({
          label: formatMessage({ id: `fixtureType.${type}` }),
          value: type,
        }))}
        onChange={(fixtureType) => setFilters({ fixtureType: fixtureType as FixtureTypes | undefined })}
        placeholder={formatMessage({ id: "type" })}
        {...selectProps}
      />
      <FilterSelectFixtureStatus
        onChange={(fixtureStatus) => setFilters({ status: fixtureStatus as FixtureStatuses | undefined })}
        width={170}
        multiple
        placeholder={formatMessage({
          id: "dataTable.filters.fixtuerStatus.allOption",
        })}
      />
      <SelectVenue
        value={filters.venue}
        onChange={(venueOption) => setFilters({ venue: venueOption })}
        placeholder={formatMessage({ id: "venue" })}
        seasonId={seasonId}
        {...selectProps}
      />
      <SelectStage
        value={filters.stage}
        onChange={(stage) => setFilters({ stage })}
        placeholder={formatMessage({ id: "stage" })}
        {...selectProps}
      />
      <SelectPool
        value={filters.pool}
        onChange={(pool) => setFilters({ pool })}
        placeholder={formatMessage({ id: "pool" })}
        {...selectProps}
      />
      <SelectSeries
        value={filters.series}
        onChange={(series) => setFilters({ series })}
        placeholder={formatMessage({ id: "series" })}
        {...selectProps}
      />
    </div>
  );
};
